import React, { useState, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import { Popover as AntdPopover, PopoverProps as AntdPopoverProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import cn from './styles.module.scss';

export interface PopoverProps extends Omit<AntdPopoverProps, 'title'> {
  /**
   * When `true`, renders a close icon in the title and prevents the popover
   * from closing on outside click. The popup must be dismissed via the close
   * icon. Antd v6's `Popover` has no native equivalent — this is the wrapper's
   * one custom feature.
   */
  closable?: boolean;
  /**
   * Title is narrowed to `ReactNode` (antd's signature also allows
   * `RenderFunction`). Render-function titles can't be wrapped in JSX for the
   * `closable` close icon, so this wrapper deliberately forbids them.
   */
  title?: React.ReactNode;
}

export const Popover = ({
  content,
  title: propsTitle,
  open,
  onOpenChange,
  children,
  closable,
  classNames,
  ...props
}: PopoverProps) => {
  const [visible, setVisible] = useState(open ?? false);

  useEffect(() => {
    if (visible !== open) {
      setVisible(Boolean(open));
    }
  }, [open]);

  const handleVisibleChange = (newVisible: boolean) => {
    if (closable && !newVisible) {
      return;
    }

    setVisible(newVisible);

    if (onOpenChange) {
      onOpenChange(newVisible);
    }
  };

  const title = useMemo(() => {
    if (!closable) {
      return propsTitle;
    }

    return (
      <div className={cn.title}>
        <div>{propsTitle}</div>
        <CloseOutlined
          onClick={() => setVisible(false)}
        />
      </div>
    );
  }, [propsTitle, closable]);

  return (
    <AntdPopover
      content={content}
      title={title}
      onOpenChange={handleVisibleChange}
      open={visible}
      classNames={typeof classNames === 'function'
        ? classNames
        : {
          ...classNames,
          content: classnames(cn.content, classNames?.content),
        }
    }
      {...props}
    >
      {children}
    </AntdPopover>
  );
};
